import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { usePageTitle } from '@/hooks/usePageTitle';
import {
  Smartphone,
  MessageCircle,
  Heart,
  Shield,
  Wine,
  DollarSign,
  Star,
  Clock,
  Users,
  ArrowRight,
  Check,
  ChefHat,
  Sparkles,
  Download,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEOHead from '@/components/SEOHead';

const Customers = () => {
  // Set SEO-optimized title for Customers landing page
  usePageTitle('customers');

  const [currentScreen, setCurrentScreen] = useState(0);
  const [isZoomed, setIsZoomed] = useState(false);

  const appScreenshots = [
    { image: '/images/customer-app/1_introscreen.jpg', alt: 'Dine AI Welcome Screen - Choose Your Occasion' },
    { image: '/images/customer-app/2_celebrations.jpg', alt: 'AI Recommendations - Celebration Menu' },
    { image: '/images/customer-app/4_main_course.jpg', alt: 'Grilled Mahi Mahi - Chef Recommendations' },
    { image: '/images/customer-app/5_cocktails.jpg', alt: 'Cocktail Recommendations - Cucumber Gin Fizz' },
    { image: '/images/customer-app/6_desserts.jpg', alt: 'Dessert Menu - AI Personalized Suggestions' }
  ];

  // Auto-rotate screenshots every 8 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentScreen((prev) => (prev + 1) % appScreenshots.length);
    }, 8000);
    return () => clearInterval(timer);
  }, []);

  const nextScreen = () => setCurrentScreen((prev) => (prev + 1) % appScreenshots.length);
  const prevScreen = () => setCurrentScreen((prev) => (prev - 1 + appScreenshots.length) % appScreenshots.length);

  const openZoom = () => {
    setIsZoomed(true);
    // Add history entry so back button can close modal
    window.history.pushState({ modal: 'zoom' }, '');
  };

  const closeZoom = () => {
    setIsZoomed(false);
    // Remove history entry if it exists
    if (window.history.state?.modal === 'zoom') {
      window.history.back();
    }
  };

  // Handle browser back button
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      if (isZoomed) {
        event.preventDefault();
        setIsZoomed(false);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isZoomed]);

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  const staggerChildren = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  // Features data
  const features = [
    {
      icon: Heart,
      title: "AI Recommendations",
      description: "Our AI learns your unique tastes and preferences. Creates personalized suggestions just for you.",
      highlight: "94% satisfaction rate",
      color: "text-emerald-400"
    },
    {
      icon: Shield,
      title: "Allergy-Safe Dining",
      description: "Set dietary restrictions once, we'll always remember. Every suggestion guaranteed safe.",
      highlight: "100% allergy safe",
      color: "text-blue-400"
    },
    {
      icon: Wine,
      title: "Perfect Pairings",
      description: "Expertly matched beverage recommendations for every meal. Elevate your dining experience!",
      highlight: "AI-curated pairings",
      color: "text-purple-400"
    },
    {
      icon: DollarSign,
      title: "Smart Budget Control",
      description: "Quality recommendations that respect your budget. Smart spending made simple!",
      highlight: "Stay within budget",
      color: "text-dineai-yellow"
    }
  ];

  // Scenarios data
  const scenarios = [
    {
      title: "Romantic Evening",
      query: "I want something romantic and shareable for our anniversary",
      response: "Perfect for your special evening! Start with our Grilled Peach Burrata - it's beautifully presented and ideal for sharing. For mains, the Seared Scallops paired with a Peach Bellini creates an elegant experience. The sweetness of the bellini complements the delicate scallops perfectly!",
      images: ["/images/60/menu/4237/v2.png", "/images/60/menu/4245/v2.png", "/images/60/menu/4261/v2.png"]
    },
    {
      title: "Culinary Adventure",
      query: "Surprise me with something unique I wouldn't normally order",
      response: "Let's take your taste buds on a journey! Try our BBQ Jackfruit Sandwich - it's our chef's fusion masterpiece that even meat lovers adore. Pair it with our Cucumber Gin Fizz for a refreshing contrast. The crisp cocktail cuts through the smoky BBQ beautifully!",
      images: ["/images/60/menu/4246/v2.png", "/images/60/menu/4262/v2.png"]
    },
    {
      title: "Comfort Food Fix",
      query: "I've had a long day - what's your most comforting dish?",
      response: "I've got just what you need! Our Lemon Herb Chicken is like a warm hug on a plate - tender, aromatic, and soul-satisfying. Add our Coconut Shrimp appetizer to start, and finish with the rich Coconut Gelato. Pure comfort from start to finish!",
      images: ["/images/60/menu/4242/v1.png", "/images/60/menu/4238/v2.png", "/images/60/menu/4257/v1.png"]
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "MobileApplication",
    "name": "Dine AI Customer App",
    "description": "Personal AI dining assistant with dietary preferences, allergy-safe recommendations, and smart budget control.",
    "applicationCategory": "LifestyleApplication",
    "operatingSystem": "iOS, Android",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "ratingCount": "1847"
    },
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD",
      "description": "Free for diners"
    }
  };

  return (
    <>
      <SEOHead 
        title="Dine AI - Your Personal AI Dining Assistant | Allergy-Safe & Personalized"
        description="Never wonder 'what should I eat?' again. AI-powered dining recommendations tailored to your taste, dietary needs, and budget. Download free app."
        keywords="AI dining assistant, personalized food recommendations, allergy-safe dining, dietary restriction app, smart restaurant ordering"
        structuredData={structuredData}
      />
      <div className="min-h-screen">
        <Header />
      
      {/* Hero Section - Primary Blue */}
      <section className="pt-32 pb-20 bg-gradient-to-b from-[#1f4ed1] to-[#2256e5] relative overflow-hidden">
        {/* Background elements */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 left-10 w-96 h-96 rounded-full bg-dineai-yellow/10 blur-3xl"></div>
          <div className="absolute bottom-10 right-20 w-80 h-80 rounded-full bg-white/5 blur-3xl"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div 
            className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center"
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
          >
            {/* Left content */}
            <div>
              <motion.div 
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6"
                variants={fadeInUp}
              >
                <Sparkles className="w-4 h-4 text-dineai-yellow" />
                <span className="text-white/90 text-sm font-medium">AI-Powered Dining</span>
              </motion.div>
              
              <motion.h1
                className="text-5xl md:text-6xl font-bold mb-6 text-white"
                variants={fadeInUp}
              >
                Your Personal{" "}
                <span className="bg-gradient-to-r from-dineai-yellow via-yellow-300 to-dineai-yellow bg-clip-text text-transparent whitespace-nowrap">
                  AI Dining Assistant
                </span>
              </motion.h1>
              
              <motion.p 
                className="text-xl text-white/80 mb-8"
                variants={fadeInUp}
              >
                Discover perfect meals tailored to your taste, dietary needs, and mood. 
                Chat with Dine AI and never wonder "what should I eat?" again.
              </motion.p>

              {/* App download buttons */}
              <motion.div
                className="flex flex-col sm:flex-row gap-4"
                variants={fadeInUp}
              >
                <a href="https://apps.apple.com/us/app/dine-ai/id6743492809" target="_blank" rel="noopener noreferrer" className="group relative transform transition-all duration-300 hover:scale-105">
                  <div className="backdrop-blur-sm p-4 rounded-xl border border-white/30 shadow-lg group-hover:shadow-xl group-hover:border-white/50 transition-all duration-300" style={{ backgroundColor: 'rgba(50, 86, 220, 0.4)' }}>
                    <img
                      src="/apple-store.png"
                      alt="Download on App Store"
                      className="h-14 w-auto"
                    />
                  </div>
                </a>
                <a href="https://play.google.com/store/apps/details?id=com.dine.ai&hl=en_US" target="_blank" rel="noopener noreferrer" className="group relative transform transition-all duration-300 hover:scale-105">
                  <div className="backdrop-blur-sm p-4 rounded-xl border border-white/30 shadow-lg group-hover:shadow-xl group-hover:border-white/50 transition-all duration-300" style={{ backgroundColor: 'rgba(50, 86, 220, 0.4)' }}>
                    <img
                      src="/google-playstore.png"
                      alt="Get it on Google Play"
                      className="h-14 w-auto"
                    />
                  </div>
                </a>
              </motion.div>

              {/* Stats */}
              <motion.div 
                className="flex gap-8 mt-8"
                variants={fadeInUp}
              >
                <div>
                  <div className="text-2xl font-bold text-dineai-yellow">AI-Powered</div>
                  <div className="text-sm text-white/70">Smart Recommendations</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-dineai-yellow">24/7</div>
                  <div className="text-sm text-white/70">Always Available</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-dineai-yellow">98.7%</div>
                  <div className="text-sm text-white/70">Accuracy Rate</div>
                </div>
              </motion.div>
            </div>

            {/* Right content - Clean screenshot carousel */}
            <motion.div
              className="relative flex justify-center"
              variants={fadeInUp}
            >
              <div className="relative w-[280px] sm:w-[300px] md:w-[320px]">
                {/* Screenshot carousel - Click to zoom */}
                <div
                  className="relative cursor-pointer group"
                  onClick={openZoom}
                >
                  <div className="relative overflow-hidden rounded-3xl shadow-2xl">
                    <AnimatePresence mode="wait">
                      <motion.img
                        key={currentScreen}
                        src={appScreenshots[currentScreen].image}
                        alt={appScreenshots[currentScreen].alt}
                        initial={{ opacity: 0, x: 100 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -100 }}
                        transition={{ duration: 0.7 }}
                        className="w-full h-auto object-contain"
                      />
                    </AnimatePresence>
                  </div>

                  {/* Click to zoom hint */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/30 rounded-3xl pointer-events-none">
                    <div className="bg-white/90 backdrop-blur-sm px-6 py-3 rounded-full shadow-lg">
                      <p className="text-black font-semibold flex items-center gap-2">
                        <Sparkles className="w-5 h-5" />
                        Click to zoom
                      </p>
                    </div>
                  </div>
                </div>

                {/* Navigation buttons - subtle */}
                <button
                  onClick={(e) => { e.stopPropagation(); prevScreen(); }}
                  className="absolute -left-4 md:-left-5 top-1/2 -translate-y-1/2 bg-dineai-yellow/70 hover:bg-dineai-yellow text-black p-2 rounded-full shadow-md transition-all hover:scale-105"
                  aria-label="Previous screen"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); nextScreen(); }}
                  className="absolute -right-4 md:-right-5 top-1/2 -translate-y-1/2 bg-dineai-yellow/70 hover:bg-dineai-yellow text-black p-2 rounded-full shadow-md transition-all hover:scale-105"
                  aria-label="Next screen"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>

                {/* Dots indicator */}
                <div className="flex justify-center gap-2 mt-8">
                  {appScreenshots.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentScreen(index)}
                      className={`h-2.5 rounded-full transition-all ${
                        index === currentScreen
                          ? 'w-10 bg-dineai-yellow'
                          : 'w-2.5 bg-white/30 hover:bg-white/50'
                      }`}
                      aria-label={`Go to screen ${index + 1}`}
                    />
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Problem/Solution Section - Secondary Blue */}
      <section className="py-20 bg-[#2256e5]">
        <div className="container mx-auto px-4">
          <motion.div 
            className="max-w-6xl mx-auto"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerChildren}
          >
            {/* Section header */}
            <motion.div className="text-center mb-16" variants={fadeInUp}>
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
                The Dining{" "}
                <span className="bg-gradient-to-r from-dineai-yellow via-yellow-300 to-dineai-yellow bg-clip-text text-transparent">
                  Dilemma
                </span>
              </h2>
              <p className="text-xl text-white/80 max-w-3xl mx-auto">
                We've all been there. Staring at menus, unsure what to order, worried about allergies, 
                or simply overwhelmed by choices.
              </p>
            </motion.div>

            {/* Problems grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
              {[
                { emoji: "😕", text: "I don't know what I'm in the mood for" },
                { emoji: "🤷", text: "What can I actually eat with my allergies?" },
                { emoji: "📱", text: "Menu descriptions don't tell me enough" },
                { emoji: "⏰", text: "I need something quick but don't know what's fast" }
              ].map((problem, index) => (
                <motion.div
                  key={index}
                  className="backdrop-blur-sm rounded-xl p-6 text-center border border-yellow-400/30 shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300"
                  style={{ backgroundColor: '#3256dc' }}
                  variants={fadeInUp}
                >
                  <div className="text-5xl mb-4">{problem.emoji}</div>
                  <p className="text-white/80">{problem.text}</p>
                </motion.div>
              ))}
            </div>

            {/* Solution */}
            <motion.div
              className="backdrop-blur-sm rounded-2xl p-8 border border-yellow-400/30 shadow-xl"
              style={{ backgroundColor: '#3256dc' }}
              variants={fadeInUp}
            >
              <div className="flex flex-col md:flex-row items-center gap-8">
                <div className="flex-1">
                  <h3 className="text-3xl font-bold mb-4 text-white">
                    Meet Dine AI,{" "}
                    <span className="text-dineai-yellow">Your Personal Dining Assistant</span>
                  </h3>
                  <p className="text-white/80 text-lg mb-6">
                    Just chat naturally about what you want. Dine AI understands your preferences, 
                    dietary needs, and mood to find your perfect meal every time.
                  </p>
                  <div className="space-y-3">
                    {[
                      "Remembers your allergies and dietary restrictions",
                      "Learns your taste preferences over time",
                      "Suggests perfect drink pairings",
                      "Knows which dishes are quick to prepare"
                    ].map((benefit, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <Check className="w-5 h-5 text-dineai-yellow flex-shrink-0" />
                        <span className="text-white/90">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex-shrink-0">
                  <MessageCircle className="w-32 h-32 text-dineai-yellow/30" />
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Interactive Scenarios Section - Primary Blue */}
      <section className="py-20 bg-[#1f4ed1]">
        <div className="container mx-auto px-4">
          <motion.div 
            className="max-w-6xl mx-auto"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerChildren}
          >
            {/* Section header */}
            <motion.div className="text-center mb-16" variants={fadeInUp}>
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
                See Dine AI{" "}
                <span className="bg-gradient-to-r from-dineai-yellow via-yellow-300 to-dineai-yellow bg-clip-text text-transparent">
                  In Action
                </span>
              </h2>
              <p className="text-xl text-white/80 max-w-3xl mx-auto">
                Real conversations, real solutions. Here's how Dine AI helps in everyday dining situations.
              </p>
            </motion.div>

            {/* Scenarios */}
            <div className="space-y-12">
              {scenarios.map((scenario, index) => (
                <motion.div
                  key={index}
                  className="bg-[#2256e5] backdrop-blur-sm rounded-2xl p-8 border border-white/20"
                  variants={fadeInUp}
                >
                  <div className={`grid md:grid-cols-2 gap-8 items-center ${index % 2 === 1 ? 'md:flex-row-reverse' : ''}`}>
                    <div className={index % 2 === 1 ? 'md:order-2' : ''}>
                      <div className="inline-block bg-dineai-yellow/20 px-3 py-1 rounded-full mb-4">
                        <span className="text-dineai-yellow text-sm font-medium">Scenario #{index + 1}</span>
                      </div>
                      <h3 className="text-2xl font-bold mb-4 text-white">{scenario.title}</h3>
                      
                      {/* Chat mockup */}
                      <div className="space-y-3">
                        <div className="bg-white/5 rounded-xl rounded-tr-none p-4 ml-auto max-w-[90%]">
                          <p className="text-white/90 text-sm">"{scenario.query}"</p>
                        </div>
                        <div className="bg-dineai-yellow/10 rounded-xl rounded-tl-none p-4 max-w-[90%]">
                          <p className="text-white/90 text-sm">{scenario.response}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className={index % 2 === 1 ? 'md:order-1' : ''}>
                      <div className="relative p-6">
                        {/* Decorative background gradient */}
                        <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-dineai-yellow/10 rounded-2xl"></div>
                        
                        {scenario.images ? (
                          <div className="relative z-10 flex gap-4 justify-center items-center flex-wrap p-4">
                            {scenario.images.map((img, imgIndex) => (
                              <motion.div 
                                key={imgIndex} 
                                className={`relative group ${scenario.images.length === 3 ? "w-[150px] h-[150px]" : scenario.images.length === 2 ? "w-[180px] h-[180px]" : "w-[250px] h-[250px]"}`}
                                whileHover={{ scale: 1.08, rotate: 2 }}
                                transition={{ type: "spring", stiffness: 300 }}
                              >
                                {/* Image container with border effect */}
                                <div className="absolute inset-0 bg-gradient-to-br from-dineai-yellow/30 to-transparent rounded-xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
                                <div className="relative bg-white/10 backdrop-blur-sm p-2 rounded-xl border border-white/20 h-full">
                                  <img 
                                    src={img} 
                                    alt={`${scenario.title} ${imgIndex + 1}`}
                                    className="w-full h-full object-cover rounded-lg"
                                  />
                                  {/* Subtle overlay gradient */}
                                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-lg pointer-events-none"></div>
                                </div>
                              </motion.div>
                            ))}
                          </div>
                        ) : (
                          <div className="relative z-10 flex justify-center p-4">
                            <motion.div 
                              className="relative group w-[280px] h-[280px]"
                              whileHover={{ scale: 1.05 }}
                              transition={{ type: "spring", stiffness: 300 }}
                            >
                              <div className="absolute inset-0 bg-gradient-to-br from-dineai-yellow/30 to-transparent rounded-xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
                              <div className="relative bg-white/10 backdrop-blur-sm p-2 rounded-xl border border-white/20 h-full">
                                <img 
                                  src={scenario.image} 
                                  alt={scenario.title}
                                  className="w-full h-full object-cover rounded-lg"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-lg pointer-events-none"></div>
                              </div>
                            </motion.div>
                          </div>
                        )}
                        
                        {/* Decorative dots pattern */}
                        <div className="absolute top-2 right-2 grid grid-cols-3 gap-1 opacity-20">
                          {[...Array(9)].map((_, i) => (
                            <div key={i} className="w-1 h-1 bg-dineai-yellow rounded-full"></div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Core Features Section - Secondary Blue */}
      <section className="py-20 bg-[#2256e5]">
        <div className="container mx-auto px-4">
          <motion.div 
            className="max-w-6xl mx-auto"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerChildren}
          >
            {/* Section header */}
            <motion.div className="text-center mb-16" variants={fadeInUp}>
              <div className="inline-block bg-dineai-yellow/20 px-4 py-2 rounded-full mb-4">
                <span className="text-dineai-yellow font-medium text-sm">CORE FEATURES</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
                Dining Intelligence{" "}
                <span className="bg-gradient-to-r from-dineai-yellow via-yellow-300 to-dineai-yellow bg-clip-text text-transparent">
                  That Works
                </span>
              </h2>
            </motion.div>

            {/* Features grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  className="group"
                  variants={fadeInUp}
                  whileHover={{ y: -5 }}
                >
                  <div className="backdrop-blur-sm rounded-xl p-6 border border-yellow-400/30 shadow-xl hover:shadow-2xl transition-all h-full" style={{ backgroundColor: '#3256dc' }}>
                    <div className="mb-4">
                      <feature.icon className={`w-12 h-12 ${feature.color}`} strokeWidth={1.5} />
                    </div>
                    <h3 className="text-xl font-bold mb-3 text-white">{feature.title}</h3>
                    <p className="text-white/70 mb-4">{feature.description}</p>
                    <div className="pt-4 border-t border-white/10">
                      <div className="text-dineai-yellow font-semibold">{feature.highlight}</div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* How It Works Section - Primary Blue */}
      <section className="py-20 bg-[#1f4ed1]">
        <div className="container mx-auto px-4">
          <motion.div 
            className="max-w-4xl mx-auto"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerChildren}
          >
            {/* Section header */}
            <motion.div className="text-center mb-16" variants={fadeInUp}>
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
                Getting Started is{" "}
                <span className="bg-gradient-to-r from-dineai-yellow via-yellow-300 to-dineai-yellow bg-clip-text text-transparent">
                  Simple
                </span>
              </h2>
            </motion.div>

            {/* Steps */}
            <div className="space-y-8">
              {[
                {
                  step: "1",
                  title: "Download the App",
                  description: "Available for iOS and Android. Free to download and use.",
                  icon: Download
                },
                {
                  step: "2",
                  title: "Chat with Dine AI",
                  description: "Tell Dine AI what you're craving, your dietary needs, or let it surprise you!",
                  icon: MessageCircle
                },
                {
                  step: "3",
                  title: "Enjoy Your Meal",
                  description: "Get personalized recommendations and discover new favorites every time.",
                  icon: Heart
                }
              ].map((step, index) => (
                <motion.div
                  key={index}
                  className="flex gap-6 items-start"
                  variants={fadeInUp}
                >
                  <div className="flex-shrink-0 w-16 h-16 bg-dineai-yellow rounded-full flex items-center justify-center">
                    <span className="text-2xl font-bold text-black">{step.step}</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold mb-2 text-white">{step.title}</h3>
                    <p className="text-white/80 text-lg">{step.description}</p>
                  </div>
                  <step.icon className="w-8 h-8 text-dineai-yellow/50 flex-shrink-0" />
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Social Proof Section - Secondary Blue */}
      <section className="py-20 bg-[#2256e5]">
        <div className="container mx-auto px-4">
          <motion.div 
            className="max-w-6xl mx-auto"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerChildren}
          >
            {/* Section header */}
            <motion.div className="text-center mb-16" variants={fadeInUp}>
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
                Loved by{" "}
                <span className="bg-gradient-to-r from-dineai-yellow via-yellow-300 to-dineai-yellow bg-clip-text text-transparent">
                  Food Lovers
                </span>
              </h2>
            </motion.div>

            {/* Testimonials grid */}
            <div className="grid md:grid-cols-3 gap-8 mb-16">
              {[
                {
                  name: "Sarah M.",
                  text: "As someone with severe nut allergies, Dine AI has been a game-changer. I can finally dine out with confidence!",
                  rating: 5
                },
                {
                  name: "Mike Chen",
                  text: "The wine pairing suggestions are spot on. It's like having a sommelier in my pocket!",
                  rating: 5
                },
                {
                  name: "Lisa Rodriguez",
                  text: "I love how Dine AI remembers my preferences. It gets better with every use!",
                  rating: 5
                }
              ].map((testimonial, index) => (
                <motion.div
                  key={index}
                  className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20"
                  variants={fadeInUp}
                >
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-dineai-yellow fill-current" />
                    ))}
                  </div>
                  <p className="text-white/90 mb-4 italic">"{testimonial.text}"</p>
                  <p className="text-white/70 font-semibold">{testimonial.name}</p>
                </motion.div>
              ))}
            </div>

            {/* Platform stats */}
            <motion.div 
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20"
              variants={fadeInUp}
            >
              <div className="grid md:grid-cols-3 gap-8 text-center">
                <div>
                  <div className="text-4xl font-bold text-dineai-yellow mb-2">Growing</div>
                  <div className="text-white/70">Restaurant Network</div>
                  <div className="text-sm text-white/50 mt-1">Expanding daily</div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-dineai-yellow mb-2">Real-Time</div>
                  <div className="text-white/70">Menu Updates</div>
                  <div className="text-sm text-white/50 mt-1">Always current</div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-dineai-yellow mb-2">98.7%</div>
                  <div className="text-white/70">Accuracy Rate</div>
                  <div className="text-sm text-white/50 mt-1">AI recommendations</div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Final CTA Section - Primary Blue */}
      <section className="py-20 bg-[#1f4ed1]">
        <div className="container mx-auto px-4">
          <motion.div 
            className="max-w-4xl mx-auto text-center"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
              Start Your AI Dining{" "}
              <span className="bg-gradient-to-r from-dineai-yellow via-yellow-300 to-dineai-yellow bg-clip-text text-transparent">
                Journey Today
              </span>
            </h2>
            <p className="text-xl text-white/80 mb-12 max-w-2xl mx-auto">
              Discover the joy of personalized dining with our growing restaurant network. 
              Download Dine AI and let our AI be your guide to amazing meals.
            </p>

            {/* App download buttons */}
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-12">
              <a href="https://apps.apple.com/us/app/dine-ai/id6743492809" target="_blank" rel="noopener noreferrer" className="group relative transform transition-all duration-300 hover:scale-110 hover:-translate-y-1">
                <div className="backdrop-blur-sm p-4 rounded-xl border-2 border-dineai-yellow shadow-xl group-hover:shadow-2xl transition-shadow duration-300" style={{ backgroundColor: '#3256dc' }}>
                  <img
                    src="/apple-store.png"
                    alt="Download on App Store"
                    className="h-14 w-auto"
                  />
                </div>
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.dine.ai&hl=en_US" target="_blank" rel="noopener noreferrer" className="group relative transform transition-all duration-300 hover:scale-110 hover:-translate-y-1">
                <div className="backdrop-blur-sm p-4 rounded-xl border-2 border-dineai-yellow shadow-xl group-hover:shadow-2xl transition-shadow duration-300" style={{ backgroundColor: '#3256dc' }}>
                  <img
                    src="/google-playstore.png"
                    alt="Get it on Google Play"
                    className="h-14 w-auto"
                  />
                </div>
              </a>
            </div>

            {/* QR Code */}
            <div className="inline-block bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
              <p className="text-white/70 mb-4 text-center font-medium">Scan to download</p>
              <div className="w-48 h-48 bg-white rounded-lg mx-auto p-2">
                <img
                  src="/images/qr-codes/qr-code.svg"
                  alt="Scan QR Code to Download Dine AI App"
                  className="w-full h-full"
                />
              </div>
            </div>

            {/* Final tagline */}
            <p className="text-white/60 text-sm mt-12">
              Available at participating restaurants. Free to download and use.
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />

      {/* Zoom Modal */}
      <AnimatePresence>
        {isZoomed && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-sm p-4"
            onClick={closeZoom}
          >
            {/* Close button */}
            <button
              onClick={closeZoom}
              className="absolute top-6 right-6 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full transition-all hover:scale-110 z-10"
              aria-label="Close zoom"
            >
              <Check className="w-6 h-6 rotate-45" />
            </button>

            {/* Navigation in zoom view */}
            <button
              onClick={(e) => { e.stopPropagation(); prevScreen(); }}
              className="absolute left-6 top-1/2 -translate-y-1/2 bg-dineai-yellow/90 hover:bg-dineai-yellow text-black p-4 rounded-full shadow-lg transition-all hover:scale-110 z-10"
              aria-label="Previous screen"
            >
              <ChevronLeft className="w-8 h-8" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); nextScreen(); }}
              className="absolute right-6 top-1/2 -translate-y-1/2 bg-dineai-yellow/90 hover:bg-dineai-yellow text-black p-4 rounded-full shadow-lg transition-all hover:scale-110 z-10"
              aria-label="Next screen"
            >
              <ChevronRight className="w-8 h-8" />
            </button>

            {/* Zoomed screenshot - Portrait aspect ratio for phone screenshots */}
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="max-w-sm max-h-[85vh] flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <AnimatePresence mode="wait">
                <motion.img
                  key={currentScreen}
                  src={appScreenshots[currentScreen].image}
                  alt={appScreenshots[currentScreen].alt}
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ duration: 0.5 }}
                  className="max-w-full max-h-full object-contain rounded-2xl shadow-2xl"
                />
              </AnimatePresence>
            </motion.div>

            {/* Screen indicator */}
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full">
              <p className="text-white font-medium">
                {currentScreen + 1} / {appScreenshots.length}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
    </>
  );
};

export default Customers;